import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, UserRole } from '../types';

interface AuthStore {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  setAuthenticated: (authenticated: boolean) => void;
  setLoading: (loading: boolean) => void;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  clear: () => void;
}

/**
 * Zustand store for authentication state
 * Integrated with MSAL authentication
 * Note: Actual authentication is handled by MSAL, this store is for UI state
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,

      setUser: (user: User | null) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      setAuthenticated: (authenticated: boolean) =>
        set({
          isAuthenticated: authenticated,
        }),

      setLoading: (loading: boolean) =>
        set({
          isLoading: loading,
        }),

      hasRole: (role: UserRole): boolean => {
        const { user } = get();
        return user?.role === role;
      },

      hasAnyRole: (roles: UserRole[]): boolean => {
        const { user } = get();
        return user ? roles.includes(user.role) : false;
      },

      clear: () =>
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        // Only persist user data, not loading state
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
